import { useState } from 'react'
import { ArrowRight, Check, Loader2 } from 'lucide-react'
import { lumi } from '@/lib/lumi'

interface FormState {
  fullName: string
  email: string
  phone: string
  scope: string
}

const EMPTY: FormState = {
  fullName: '',
  email: '',
  phone: '',
  scope: '',
}

const labelClass = 'text-xs font-semibold uppercase tracking-[0.14em] text-forest-950/55'

const fieldClass =
  'w-full rounded-2xl border border-bone-300 bg-bone-50 px-4 py-3 text-sm text-forest-950 placeholder:text-forest-950/35 transition focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/25'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export default function EstimateForm() {
  const [form, setForm] = useState<FormState>(EMPTY)
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [error, setError] = useState('')

  const set = (key: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [key]: event.target.value }))
    if (error) setError('')
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (status === 'sending') return

    if (!form.fullName.trim() || !form.email.trim() || !form.scope.trim()) {
      setError('Please add your name, email address, and a short description of your project.')
      return
    }

    if (!EMAIL_PATTERN.test(form.email.trim())) {
      setError('Please enter a valid email address so we can reply.')
      return
    }

    setStatus('sending')
    setError('')
    try {
      const phone = form.phone.trim()
      await lumi.entities.estimates.create({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        scope: form.scope.trim(),
        ...(phone ? { phone } : {}),
      })
      setStatus('sent')
      setForm(EMPTY)
    } catch (err) {
      setStatus('idle')
      setError(err instanceof Error && err.message
        ? `We could not send your request: ${err.message}`
        : 'We could not send your request. Please call us instead.')
    }
  }

  if (status === 'sent') {
    return (
      <div className="flex h-full min-h-[22rem] flex-col items-start justify-center gap-4 rounded-4xl border border-pine/25 bg-pine/5 p-8">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-pine text-bone-50">
          <Check className="h-6 w-6" strokeWidth={2.2} />
        </span>
        <h3 className="font-display text-2xl text-forest-950">Details received.</h3>
        <p className="max-w-sm text-sm leading-relaxed text-forest-950/70">
          Thanks for reaching out. We read every request personally and will follow up by email to
          talk through your project and schedule a site visit.
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="text-sm font-semibold text-pine underline decoration-pine/30 underline-offset-4 transition hover:decoration-pine"
        >
          Send another request
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <label className="flex flex-col gap-2">
        <span className={labelClass}>Full Name</span>
        <input
          type="text"
          name="fullName"
          autoComplete="name"
          value={form.fullName}
          onChange={set('fullName')}
          placeholder="Your Name"
          className={fieldClass}
        />
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex min-w-0 flex-col gap-2">
          <span className={labelClass}>Email</span>
          <input
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            value={form.email}
            onChange={set('email')}
            placeholder="you@example.com"
            className={fieldClass}
          />
        </label>

        <label className="flex min-w-0 flex-col gap-2">
          <span className={labelClass}>
            Phone <span className="normal-case tracking-normal text-forest-950/40">(optional)</span>
          </span>
          <input
            type="tel"
            name="phone"
            autoComplete="tel"
            inputMode="tel"
            value={form.phone}
            onChange={set('phone')}
            placeholder="(252) 000-0000 (For text)"
            className={`${fieldClass} placeholder:text-[13px]`}
          />
        </label>
      </div>

      <label className="flex flex-col gap-2">
        <span className={labelClass}>Project Notes &amp; Location</span>
        <textarea
          name="scope"
          rows={5}
          value={form.scope}
          onChange={set('scope')}
          placeholder="Tell us about your project ideas, scope, and general location..."
          maxLength={1200}
          className={`${fieldClass} resize-none`}
        />
      </label>

      {error && (
        <p role="alert" className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'sending'}
        className="group mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-pine px-7 py-3.5 text-sm font-semibold text-bone-50 transition hover:bg-forest-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine focus-visible:ring-offset-2 focus-visible:ring-offset-bone-50 disabled:cursor-wait disabled:opacity-70"
      >
        {status === 'sending' ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending…
          </>
        ) : (
          <>
            Send Project Details
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
          </>
        )}
      </button>

      <p className="text-xs leading-relaxed text-forest-950/50">
        We respect your privacy and never share your details. If a phone number is provided, you
        agree to receive SMS communications regarding your project inquiry. Reply STOP to opt out.
      </p>
    </form>
  )
}
